'use client'

import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { apiClient } from '@/lib/api/client'
import { customerIdentityValue } from '@/lib/customer-identity'
import type { CustomerSignInEmailStatus, Customer as HelmCustomer } from '@/lib/api/types'
import { SignInEmailPanel } from '@/src/components/SignInEmailPanel'

interface CustomerDetailsProps {
  customer: HelmCustomer
  /** Admins only: the confirmed login that signs in with the customer's email. */
  signIn?: CustomerSignInEmailStatus | null
}

/** The customer's identity fields, read-only. Admins change them with Edit in the page header. */
export function CustomerDetails({ customer, signIn = null }: CustomerDetailsProps) {
  return (
    <div className="space-y-6">
      {customer.is_placeholder && (
        <Alert>
          <AlertDescription>
            Provisional identity. Confirm the customer's name, email, phone and TIN before using
            this profile for correspondence or invoicing. Completing this profile keeps{' '}
            {customer.customer_number || 'its customer ID'} unchanged. If this is an existing
            customer, review the inquiry association instead of overwriting this identity.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Name</Label>
          <p className="mt-1 font-medium">{customer.name}</p>
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Type</Label>
          <p className="mt-1 font-medium">{customer.type}</p>
        </div>
        {customer.type === 'BUSINESS' && (
          <>
            <div>
              <Label className="text-muted-foreground text-xs uppercase">Company Name</Label>
              <p className="mt-1 font-medium">{customer.company_name || '-'}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs uppercase">Contact Person</Label>
              <p className="mt-1 font-medium">{customer.contact_person || '-'}</p>
            </div>
          </>
        )}
        <div>
          <Label className="text-muted-foreground text-xs uppercase">TIN Number</Label>
          <p className="mt-1 font-mono">
            {customerIdentityValue(customer, 'tin_number') || 'Not confirmed'}
          </p>
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Phone</Label>
          <p className="mt-1">{customerIdentityValue(customer, 'phone') || 'Not confirmed'}</p>
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">Email</Label>
          <p className="mt-1">{customerIdentityValue(customer, 'email') || 'Not confirmed'}</p>
        </div>
        <div>
          <Label className="text-muted-foreground text-xs uppercase">WhatsApp Group ID</Label>
          <p className="mt-1 font-mono text-sm">{customer.whatsapp_group_id || '-'}</p>
        </div>
      </div>

      {signIn?.login && (
        <div className="space-y-3 border-t pt-4">
          <div>
            <Label className="text-muted-foreground text-xs uppercase">Helm sign-in</Label>
            <p className="mt-1">Signs in to Helm with {signIn.login.email}</p>
          </div>
          <SignInEmailPanel
            idPrefix="customer-details"
            who="customer"
            currentEmail={signIn.login.email}
            pending={signIn.pending}
            actions={{
              request: async (newEmail) =>
                (await apiClient.requestCustomerSignInEmailChange(customer.id, newEmail)).pending,
              resend: async () =>
                (await apiClient.resendCustomerSignInEmailChange(customer.id)).pending,
              cancel: async () => {
                await apiClient.cancelCustomerSignInEmailChange(customer.id)
              },
            }}
          />
        </div>
      )}

      <div className="border-t pt-4 text-sm text-muted-foreground">
        <p>Created: {new Date(customer.created_at).toLocaleDateString()}</p>
        <p>Last updated: {new Date(customer.updated_at).toLocaleDateString()}</p>
      </div>
    </div>
  )
}
